import React, { useMemo, useState, useEffect } from "react";
import { Space, Avatar, Typography, Form, Select, Flex, Button, Tag, Checkbox, Segmented, Dropdown, DropdownProps, MenuProps, List, Popover, Spin, Empty, Card } from "antd";
import moment from "moment";
import MasterComponent from "../../components/Forms";
import { MasterConfigs } from "../../interfaces/entity";
import ProfileCard from "../../components/ContactInfo/ProfileCard";
import { constants } from "../../constants/common";
import DrawerComponent from "../../components/Forms/Drawer";
import LeadDetails from "./LeadDetails";
import { BorderOuterOutlined, DownOutlined, EditOutlined, OrderedListOutlined, TableOutlined, UserOutlined, CheckOutlined, PhoneOutlined, ReloadOutlined, CalendarOutlined, CheckCircleOutlined, ClockCircleOutlined, UploadOutlined } from "@ant-design/icons";
import FormSetup from "../../components/Forms/FormSetup";
import { useDispatch, useSelector } from "react-redux";
import { getData } from "../../actions/apiCall";
import { postData } from "../../actions/developer";
import SmallProfileCard from "../../components/ContactInfo/SmallProfileCard";
import { createActivitiesColumn } from "../../utils/activitiesColumn";
import BulkUploadModal from "../../components/Leads/BulkUploadModal";
const { Text, Title } = Typography;

const items: MenuProps['items'] = [
    {
        key: 'table',
        label: <TableOutlined />,
    },
    {
        key: 'kanban',
        label: <BorderOuterOutlined />,
    }
];

const Leads = () => {
    const [openDrawer, setOpenDrawer] = React.useState(false);
    const [selectedLead, setSelectedLead] = React.useState(null);
    const [converted, setConverted] = React.useState(false);
    const [createDeal, setCreateDeal] = React.useState(false);
    const [currentView, setCurrentView] = React.useState('table');
    const [summary, setSummary] = useState<any>(null);
    const [bulkUploadOpen, setBulkUploadOpen] = React.useState(false);

    // States for upcoming activities
    const [upcomingActivities, setUpcomingActivities] = useState<{ [key: string]: { date: string | Date; hasUpcoming: boolean } }>({});

    const dispatch = useDispatch();
    const { refreshTable, user } = useSelector((app: any) => app?.app || {});
    const [tableData, setTableData] = useState<any[]>([]);
    const isAdmin = user?.designation === 'Admin';


    const sharedProps: DropdownProps = {
        menu: { items },
        placement: 'bottomLeft',
    };

    // Refresh upcoming activities when table refreshes
    useEffect(() => {
        if (refreshTable && tableData.length > 0) {
            // Extract upcoming activities from table data (comes from API)
            extractUpcomingActivitiesFromRecords(tableData);
        }
    }, [refreshTable, tableData.length]);

    // Map model names to refType for tasks API
    const getRefType = (modelName: string): string => {
        const mapping: { [key: string]: string } = {
            'leads': 'Leads',
            'contacts': 'Contacts',
            'accounts': 'Businesses',
            'deals': 'Deals',
        };
        return mapping[modelName.toLowerCase()] || modelName;
    };


    // Extract upcoming activities info from leads API response
    const extractUpcomingActivitiesFromRecords = (records: any[]) => {
        if (!records || records.length === 0) return;

        const activitiesMap: { [key: string]: { date: string | Date | null; hasUpcoming: boolean } } = {};

        records.forEach((record) => {
            if (!record?._id) return;
            const recordKey = `leads-${record._id}`;

            // Use upcomingActivity from API response if available
            if (record.upcomingActivity) {
                activitiesMap[recordKey] = {
                    date: record.upcomingActivity.upcomingDate || null,
                    hasUpcoming: record.upcomingActivity.hasUpcoming || false
                };
            } else {
                // Default to no upcoming activities if not provided
                activitiesMap[recordKey] = {
                    date: null,
                    hasUpcoming: false
                };
            }
        });

        setUpcomingActivities(prev => ({
            ...prev,
            ...activitiesMap
        }));
    };

    const config: MasterConfigs = useMemo(() => {
        return {
            api: () => '/leads',
            queryParams: {
                andOr: 'and', // Use 'and' to combine filters
            },
            title: 'Leads',
            setInitialForm: () => {
                // For normal users, set default leadOwner to current user when creating new lead
                if (!isAdmin && user?._id) {
                    return { leadOwner: user._id };
                }
                return {};
            },
            activitiesColumn: {
                enabled: true,
                modelName: 'leads',
                title: 'Activities',
                width: 120,
                fixed: 'left',
                align: 'center',
                color: 'green'
            },
            onDataLoad: (data: any) => {
                // Store table data and extract upcoming activities info from API response
                // data can be either an array or an object with data array
                const records = Array.isArray(data) ? data : (data?.data || []);
                if (records && Array.isArray(records) && records.length > 0) {
                    setTableData(records);
                    extractUpcomingActivitiesFromRecords(records);
                }
            },
            onSummaryLoad: (summaryData: any) => {
                setSummary(summaryData);
            },
            renderSummary: () => {
                if (!summary) return null;
                return (
                    <Flex gap={12} wrap="wrap" style={{ width: '100%', marginBottom: 16 }}>
                        <Card
                            size="small"
                            style={{ flex: 1, minWidth: 200, maxWidth: 300, backgroundColor: '#fff', borderLeft: '4px solid #1890ff' }}
                        >
                            <Flex align="center" gap={8} style={{ justifyContent: 'space-between' }}>
                                <Text type="secondary" style={{ fontSize: '12px', margin: 0 }}>
                                    Total Leads:
                                </Text>
                                <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
                                    {summary.total || 0}
                                </Title>
                            </Flex>
                        </Card>
                        <Card
                            size="small"
                            style={{ flex: 1, minWidth: 200, maxWidth: 300, backgroundColor: '#fff', borderLeft: '4px solid #52c41a' }}
                        >
                            <Flex align="center" gap={8} style={{ justifyContent: 'space-between' }}>
                                <Text type="secondary" style={{ fontSize: '12px', margin: 0 }}>
                                    Converted:
                                </Text>
                                <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
                                    {summary.converted || 0}
                                </Title>
                            </Flex>
                        </Card>
                        <Card
                            size="small"
                            style={{ flex: 1, minWidth: 200, maxWidth: 300, backgroundColor: '#fff', borderLeft: '4px solid #faad14' }}
                        >
                            <Flex align="center" gap={8} style={{ justifyContent: 'space-between' }}>
                                <Text type="secondary" style={{ fontSize: '12px', margin: 0 }}>
                                    Non-Converted:
                                </Text>
                                <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
                                    {summary.nonConverted || 0}
                                </Title>
                            </Flex>
                        </Card>
                        <Card
                            size="small"
                            style={{ flex: 1, minWidth: 200, maxWidth: 300, backgroundColor: '#fff', borderLeft: '4px solid #722ed1' }}
                        >
                            <Flex align="center" gap={8} style={{ justifyContent: 'space-between' }}>
                                <Text type="secondary" style={{ fontSize: '12px', margin: 0 }}>
                                    Conversion Rate:
                                </Text>
                                <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
                                    {summary.conversionRate || '0.00'}%
                                </Title>
                            </Flex>
                        </Card>
                    </Flex>
                );
            },
            extraControles: [
                <Dropdown {...sharedProps} key="view-select-dropdown">
                    <Button className="flex items-center">
                        <Space align="center">
                            {currentView === 'table' ? <TableOutlined /> : <BorderOuterOutlined />}
                            <DownOutlined />
                        </Space>
                    </Button>
                </Dropdown>
            ],
            getFilterColumns: () => {
                const filterColumns: any[] = [
                    {
                        label: 'Lead Status',
                        name: 'leadStatus',
                        type: 'select',
                        span: 4,
                        selectProps: {
                            allowClear: true,
                            placeholder: 'Select Status',
                        },
                        children: [
                            { name: 'Converted', value: 'converted' },
                            ...constants.LEAD_STATUS.filter(ele => ele.name !== 'None').map(status => ({
                                name: status.name,
                                value: status.value
                            }))
                        ]
                    },
                    {
                        label: 'Lead Source',
                        name: 'leadSource',
                        type: 'select',
                        span: 4,
                        selectProps: {
                            allowClear: true,
                            placeholder: 'Select Source',
                            showSearch: true,
                        },
                        children: constants.LEAD_SOURCES.map(source => ({
                            name: source.name,
                            value: source.value
                        }))
                    },
                    {
                        label: 'Lead Type',
                        name: 'LeadsTypes',
                        type: 'select',
                        isCombo: true,
                        span: 4,
                        selectProps: {
                            allowClear: true,
                            placeholder: 'Select Lead Type',
                            showSearch: true,
                        },
                    },
                    {
                        label: 'Rating',
                        name: 'rating',
                        type: 'select',
                        span: 4,
                        selectProps: {
                            allowClear: true,
                            placeholder: 'Select Rating',
                        },
                        children: constants.RATINGS.map(rating => ({
                            name: rating,
                            value: rating
                        }))
                    },
                ];

                // Lead Owner filter - only visible to Admin
                if (isAdmin) {
                    filterColumns.push({
                        label: 'Lead Owner',
                        name: 'leadOwner',
                        type: 'select',
                        isCombo: true,
                        span: 4,
                        selectProps: {
                            allowClear: true,
                            placeholder: 'Select Lead Owner',
                            showSearch: true,
                        },
                    });
                }

                filterColumns.push({
                    label: 'Company',
                    name: 'company',
                    type: 'text',
                    span: 4,
                    placeholder: 'Search by company name',
                });

                // Date range filters for created date
                filterColumns.push({
                    label: 'Start Date',
                    name: 'startDate',
                    type: 'date',
                    span: 4,
                    placeholder: 'Select start date',
                });
                filterColumns.push({
                    label: 'End Date',
                    name: 'endDate',
                    type: 'date',
                    span: 4,
                    placeholder: 'Select end date',
                });

                return filterColumns;
            },
            modalTitle: (text) => text + ' Lead',
            isActionTab: true,
            getColumns: () => {
                const columns: any[] = [];

                // Add Activities column if enabled in config
                const activitiesColumn = createActivitiesColumn(config.activitiesColumn);
                if (activitiesColumn) {
                    columns.push(activitiesColumn);
                }

                // Add other columns
                columns.push(
                    {
                        title: 'Lead Name',
                        dataIndex: '',
                        key: '',
                        width: 200,
                        fixed: 'left',
                        render: (text, record) => <div onClick={() => handleLeadClick(record)}>
                            <SmallProfileCard
                                name={[record?.preName, record?.firstName, record?.lastName].filter(Boolean).join(' ')}
                                avatar={record?.profilePhoto ? record?.profilePhoto : undefined}
                                subTitle={record?.title}
                            />
                        </div>
                    },
                    {
                        title: 'Company',
                        dataIndex: 'company',
                        key: 'company',
                        width: 150,
                        ellipsis: true,
                    },
                    {
                        width: 150,
                        title: 'Email',
                        dataIndex: 'email',
                        key: 'email',
                    },
                    {
                        width: 150,
                        title: 'Phone',
                        dataIndex: 'phone',
                        key: 'phone',
                    },
                    {
                        title: 'Lead Type',
                        dataIndex: 'LeadsTypes',
                        key: 'LeadsTypes',
                        width: 150,
                        ellipsis: true,
                        render: (text) => text ? text.name : 'N/A',
                    },
                    {
                        title: 'Lead Source',
                        dataIndex: 'leadSource',
                        key: 'leadSource',
                        width: 100,
                    },
                    {
                        title: 'Lead Owner',
                        dataIndex: 'leadOwner',
                        key: 'leadOwner',
                        render: (text) => <SmallProfileCard
                            name={constants.GET_FULL_NAME(text)}
                            avatar={text?.avatar ? text?.avatar : undefined}
                            subTitle={text?.employeeID || text?.workEmail}
                        />,
                        width: 150
                    },
                    {
                        title: 'Created At',
                        dataIndex: 'createdAt',
                        key: 'createdAt',
                        render: (text) => moment(text).format('DD MMM YYYY HH:mm'),
                        align: 'center',
                        width: 150,
                    }
                );

                return columns;
            },
            getFormColumns: () => [
                {
                    label: 'Profile Photo',
                    name: 'profilePhoto',
                    type: 'file',
                    uploadProps: {
                        accept: 'image/*',
                        maxCount: 1,
                        listType: 'picture-circle',
                    }
                },
                {
                    type: 'custom',
                    span: 24,
                    formItem: (form) => <Title level={5} className="font-bold border-b pb-2">Lead Information</Title>
                },
                {
                    label: 'Lead Type',
                    name: 'LeadsTypes',
                    rules: [{ required: true, message: 'Lead Type is required' }],
                    type: 'select',
                    isCombo: true,
                    span: 12,
                },
                { span: 12 },
                {
                    label: 'Lead Owner',
                    name: 'leadOwner',
                    type: 'select',
                    isCombo: true,
                    span: 12,
                },
                {
                    label: 'Company',
                    name: 'company',
                    type: 'text',
                    rules: [{ required: true, message: 'Company is required' }],
                    span: 12,
                },
                {
                    label: 'First Name',
                    name: 'firstName',
                    type: 'text',
                    inputProps: {
                        addonBefore: <Form.Item name="preName">
                            <Select style={{ width: 100 }} placeholder="Title">
                                {constants.PRENAME.map(ele => <Select.Option key={ele.value} value={ele.value}>{ele.name}</Select.Option>)}
                            </Select>
                        </Form.Item>
                    },
                    rules: [{ required: true, message: 'First Name is required' }],
                    span: 12,
                },
                {
                    label: 'Last Name',
                    name: 'lastName',
                    type: 'text',
                    span: 12,
                },
                {
                    label: 'Title',
                    name: 'title',
                    type: 'text',
                    span: 12,
                },
                {
                    label: 'Email',
                    name: 'email',
                    type: 'text',
                    span: 12,
                    rules: [
                        {
                            validator: (_, value) => {
                                if (!value) return Promise.resolve();
                                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                                return emailRegex.test(value)
                                    ? Promise.resolve()
                                    : Promise.reject(new Error('Enter a valid email address'));
                            },
                        },
                    ],
                },
                {
                    label: 'Phone',
                    name: 'phone',
                    type: 'text',
                    span: 12,
                    rules: [
                        {
                            validator: (_, value) => {
                                if (!value) return Promise.resolve();
                                const phoneRegex = /^[0-9]{10,15}$/;
                                return phoneRegex.test(value)
                                    ? Promise.resolve()
                                    : Promise.reject(new Error('Enter a valid phone number'));
                            },
                        },
                    ],
                },
                {
                    label: 'Fax',
                    name: 'fax',
                    type: 'text',
                    span: 12,
                    rules: [
                        {
                            validator: (_, value) => {
                                if (!value) return Promise.resolve();
                                const faxRegex = /^[0-9+\-().\s]{6,20}$/;
                                return faxRegex.test(value)
                                    ? Promise.resolve()
                                    : Promise.reject(new Error('Enter a valid fax number'));
                            },
                        },
                    ],
                },
                {
                    label: 'Mobile',
                    name: 'mobile',
                    type: 'text',
                    span: 12,
                    rules: [
                        {
                            validator: (_, value) => {
                                if (!value) return Promise.resolve();
                                const mobileRegex = /^[0-9]{10,15}$/;
                                return mobileRegex.test(value)
                                    ? Promise.resolve()
                                    : Promise.reject(new Error('Enter a valid mobile number'));
                            },
                        },
                    ],
                },
                {
                    label: 'Website',
                    name: 'website',
                    type: 'text',
                    rules: [
                        {
                            validator: (_, value) => {
                                if (!value) return Promise.resolve();
                                try {
                                    new URL(value);
                                    return Promise.resolve();
                                } catch {
                                    return Promise.reject(new Error('Enter a valid website URL'));
                                }
                            },
                        },
                    ],
                    span: 12,
                },
                {
                    label: 'Lead Source',
                    name: 'leadSource',
                    type: 'select',
                    span: 12,
                    selectProps: {
                        showSearch: true,
                        optionFilterProp: 'children',
                        allowClear: true,
                    },
                    children: constants.LEAD_SOURCES
                },
                {
                    label: 'Lead Status',
                    name: 'leadStatus',
                    type: 'select',
                    span: 12,
                    selectProps: {
                        showSearch: true,
                        optionFilterProp: 'children',
                        allowClear: true,
                    },
                    children: constants.LEAD_STATUS,
                },
                {
                    label: 'Industry',
                    name: 'industry',
                    type: 'select',
                    span: 12,
                    selectProps: {
                        showSearch: true,
                        allowClear: true,
                        optionFilterProp: 'children',
                    },
                    children: constants.INDUSTRIES.map(industry => ({ name: industry, value: industry })),
                },
                {
                    label: 'No. of Employees',
                    name: 'noOfEmployees',
                    type: 'text',
                    span: 12,
                },
                {
                    label: 'Annual Revenue  ',
                    name: 'annualRevenue',
                    type: 'text',
                    span: 12,
                },
                {
                    label: 'Rating',
                    name: 'rating',
                    type: 'select',
                    span: 12,
                    selectProps: {
                        showSearch: true,
                        allowClear: true,
                        optionFilterProp: 'children',
                    },
                    children: constants.RATINGS.map(rating => ({ name: rating, value: rating })),
                },
                {
                    label: 'Email Opt Out ',
                    name: 'emailOptOut',
                    type: 'checkbox',
                    span: 12,
                },
                {
                    label: 'Secondary Email',
                    name: 'secondaryEmail',
                    span: 12,
                    type: 'text',
                    rules: [
                        {
                            validator: (_, value) => {
                                if (!value) return Promise.resolve();
                                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                                return emailRegex.test(value)
                                    ? Promise.resolve()
                                    : Promise.reject(new Error('Enter a valid email address'));
                            },
                        },
                    ],
                },
                {
                    span: 12,
                },
                {
                    label: 'Twitter',
                    name: 'twitter',
                    type: 'text',
                    span: 12,
                },
                {
                    label: 'LinkedIn Profile URL (User)',
                    name: 'linkedinProfileUrl',
                    type: 'text',
                    span: 12,
                    rules: [
                        {
                            validator: (_, value) => {
                                if (!value) return Promise.resolve();
                                try {
                                    const url = new URL(value);
                                    if (url.hostname.includes('linkedin.com')) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('Enter a valid LinkedIn profile URL'));
                                } catch {
                                    return Promise.reject(new Error('Enter a valid LinkedIn profile URL'));
                                }
                            },
                        },
                    ],
                },
                {
                    label: 'LinkedIn Profile URL (Company)',
                    name: 'companyLinkedinProfileUrl',
                    type: 'text',
                    span: 12,
                    rules: [
                        {
                            validator: (_, value) => {
                                if (!value) return Promise.resolve();
                                try {
                                    const url = new URL(value);
                                    if (url.hostname.includes('linkedin.com')) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('Enter a valid LinkedIn profile URL'));
                                } catch {
                                    return Promise.reject(new Error('Enter a valid LinkedIn profile URL'));
                                }
                            },
                        },
                    ],
                },
                {
                    type: 'custom',
                    span: 24,
                    formItem: (form) => <Title level={5} className="font-bold border-b pb-2">Address Information</Title>
                },
                {
                    label: 'Street',
                    name: 'street',
                    type: 'text',
                    span: 12,
                },
                {
                    label: 'City',
                    name: 'city',
                    type: 'text',
                    span: 12,
                },
                {
                    label: 'State',
                    name: 'state',
                    type: 'text',
                    span: 12,
                },
                {
                    label: 'Zip Code',
                    name: 'zipCode',
                    type: 'text',
                    span: 12,
                    rules: [
                        {
                            validator: (_, value) => {
                                if (!value) return Promise.resolve();
                                const zipRegex = /^[0-9]{4,10}$/;
                                return zipRegex.test(value)
                                    ? Promise.resolve()
                                    : Promise.reject(new Error('Enter a valid zip code'));
                            },
                        },
                    ],
                },
                {
                    label: 'Country',
                    name: 'country',
                    type: 'text',
                    span: 12,
                },
                {
                    type: 'custom',
                    span: 24,
                    formItem: (form) => <Title level={5} className="font-bold border-b pb-2">Description Information</Title>
                },
                {
                    label: 'Description',
                    name: 'description',
                    type: 'textarea',
                    span: 24,
                    textAreaProps: {
                        autoSize: { minRows: 3, maxRows: 6 },
                    }
                },

            ],
            combo: [
                {
                    api: '/leads-types',
                    name: 'LeadsTypes',
                    idKey: '_id',
                    render: (text) => text?.name || text.name
                },
                // Lead Owner combo - always include, but for normal users, default to current user
                {
                    api: '/users',
                    name: 'leadOwner',
                    idKey: '_id',
                    render: (text) => <ProfileCard
                        size={30}
                        name={constants.GET_FULL_NAME(text)}
                        avatarUrl={text?.avatar ? process.env.FILE_URL + text?.avatar : null}
                        role={null}
                    />
                },
            ],
            actions: [
                {
                    actionType: 'filter',
                    name: 'Filter',
                },
                {
                    name: 'Bulk Upload',
                    actionType: 'Custom',
                    render: () => (
                        <Button
                            icon={<UploadOutlined />}
                            onClick={() => setBulkUploadOpen(true)}
                        >
                            Bulk Upload
                        </Button>
                    )
                },
                {
                    name: 'Add Lead',
                    actionType: 'Add',
                }
            ]
        };
    }, [summary, isAdmin]);

    const convertLeadConfig: MasterConfigs = {
        api: () => '/convert',
        title: 'Leads',
        modalTitle: (text) => 'Convert Lead',
        getFormColumns: () => [
            {
                type: 'custom',
                span: 24,
                formItem: (form) => <Text className="flex gap-4">
                    Create New Account: <div className="bg-gray-200 font-semibold rounded-md px-2">{selectedLead?.company}</div>
                </Text>
            },
            {
                type: 'custom',
                span: 24,
                formItem: (form) => <Text className="flex gap-4">
                    Create New Contact: <div className="bg-gray-200 font-semibold rounded-md px-2">{selectedLead?.firstName} {selectedLead?.lastName}</div>
                </Text>
            },
            {
                type: 'custom',
                span: 24,
                name: 'createDeal',
                formItem: (form) => <Checkbox
                    name="createDeal"
                    style={{ marginBottom: 16 }}
                    checked={createDeal}
                    onChange={(e) => setCreateDeal(e.target.checked)}
                >
                    Create a new Deal for this Account.
                </Checkbox>
            },
            createDeal && {
                name: ['dealData', 'amount'],
                label: 'Amount',
                type: 'text',
                span: 12,
                inputProps: {
                    prefix: 'INR',
                },
                rules: [{
                    validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        const amountRegex = /^[0-9]+(\.[0-9]{1,2})?$/;
                        return amountRegex.test(value)
                            ? Promise.resolve()
                            : Promise.reject(new Error('Enter a valid amount'));
                    }
                }],
            },
            createDeal && {
                span: 12,
            },
            createDeal && {
                name: ['dealData', 'dealName'],
                label: 'Deal Name',
                type: 'text',
                span: 12,
                rules: [{ required: true, message: 'Deal Name is required' }],
            },
            createDeal && {
                span: 12,
            },
            createDeal && {
                name: ['dealData', 'closingDate'],
                label: 'Closing Date',
                type: 'date',
                span: 12,
                rules: [{ required: true, message: 'Closing Date is required' }],
            },
            createDeal && {
                span: 12,
            },
            createDeal && {
                name: ['dealData', 'currentStage'],
                label: 'Stage',
                type: 'select',
                isCombo: true,
                span: 12,
                selectProps: {
                    showSearch: true,
                    optionFilterProp: 'children',
                    allowClear: true,
                },
                rules: [{ required: true, message: 'Stage is required' }],
            },
            createDeal && {
                span: 12,
            },
            createDeal && {
                name: ['dealData', 'contactRole'],
                label: 'Contact Role',
                type: 'select',
                span: 12,
                children: constants.CONTACT_ROLES,
                selectProps: {
                    showSearch: true,
                    optionFilterProp: 'children',
                    allowClear: true,
                }
            },
            {
                type: 'custom',
                span: 24,
                formItem: (form) => <Text>
                    Owner of the New Records
                    <div className="font-semibold">{constants.GET_FULL_NAME(selectedLead?.leadOwner)}</div>
                </Text>
            }
        ],
        onSubmit: (id, values) => {
            const leadData = {
                createDeal: createDeal,
                ...values
            };
            return createLead(leadData);
        },
        combo: [
            {
                api: '/stages?category=Deals',
                name: 'currentStage',
                idKey: '_id',
                render: (text) => text.name
            },
        ],
        actions: []
    }

    const createLead = async (data) => {
        try {
            const resData = await dispatch(postData('/leads/convert/' + selectedLead._id, data));
            if (resData) {
                setSelectedLead(resData.data);
                setOpenDrawer(true);
                return { code: 200, message: 'Lead created successfully' };
            } else {
                return { code: 400, message: 'Failed to create lead' };
            }
        } catch (error) {
            console.error("Error creating lead:", error);
            return { code: 500, message: 'Failed to create lead' };
        } finally {
            setOpenDrawer(false);
            setConverted(false);
            setCreateDeal(false);
            setSelectedLead(null);
        }
    }


    const handleLeadClick = (record) => {
        setSelectedLead(record);
        setOpenDrawer(true);
    }

    const onConvertLead = async (lead) => {
        try {
            if (!lead) return;
            const resData = await dispatch(getData(`/leads/${lead._id}`));
            if (!resData) return;
            setSelectedLead(resData.data);
            setConverted(true);
        } catch (error) {
            console.error("Error converting lead:", error);
        }
    }

    return (
        <>
            <MasterComponent config={config} isModal={true} />
            <BulkUploadModal
                open={bulkUploadOpen}
                onCancel={() => setBulkUploadOpen(false)}
                onSuccess={() => {
                    // Refresh the table by triggering a refresh
                    if (window.dispatchEvent) {
                        window.dispatchEvent(new Event('refreshTable'));
                    }
                }}
            />
            <DrawerComponent
                title={
                    <Flex justify="space-between" align="center" gap={2} style={{ width: '95%' }}>
                        <ProfileCard
                            size={40}
                            name={`${selectedLead?.firstName || ''} ${selectedLead?.lastName || ''}`}
                            avatarUrl={selectedLead?.profilePhoto ? process.env.FILE_URL + selectedLead?.profilePhoto : null}
                            role={selectedLead?.title || 'Lead Details'}
                        />
                        {!selectedLead?.converted && <div className="flex gap-2">
                            <Button disabled type="primary">Send Email</Button>
                            <Button onClick={() => onConvertLead(selectedLead)}>Convert</Button>
                            <Button icon={<EditOutlined />} />
                        </div>}
                    </Flex>
                }
                visible={openDrawer}
                handleCancel={() => setOpenDrawer(false)}
                isView={true}
                loading={false}
                submitForm={() => { }}
                rest={{
                    width: '90%', style: {
                        background: '#eef1f9',
                        padding: '0px !important',
                    },
                    bodyStyle: { padding: 0 },
                    headerStyle: { background: '#FFFFFF', padding: '8px 10px' },
                }}
            >
                <>
                    <FormSetup open={converted} config={convertLeadConfig} isModal={true} onClose={() => { setConverted(false); setCreateDeal(false) }} />
                    {!selectedLead?.converted && <LeadDetails lead={selectedLead} />}
                    {selectedLead?.converted && <div className="p-4 bg-white rounded-md m-4">
                        <Text>The <b>Lead</b> you are trying to access has been converted already.</Text>
                        <div className="mt-4 mb-2 font-semibold text-lg">
                            Conversion Details
                        </div>

                        <div className="max-w-lg">
                            <div className="grid grid-cols-2 gap-2 border-b p-2">
                                <Text>Converted Contact</Text>
                                <Text className="text-blue-600 cursor-pointer">
                                    {selectedLead?.convertedContact ? `${selectedLead?.convertedContact.firstName || ''} ${selectedLead?.convertedContact.lastName || ''}` : 'N/A'}
                                </Text>
                            </div>
                            <div className="grid grid-cols-2 gap-2 border-b p-2">
                                <Text>Account</Text>
                                <Text className="text-blue-600 cursor-pointer">
                                    {selectedLead?.convertedAccount?.businessName || 'N/A'}
                                </Text>
                            </div>
                            <div className="grid grid-cols-2 gap-2 border-b p-2">
                                <Text>Converted Date</Text>
                                <Text className="text-blue-600 cursor-pointer">
                                    {moment(selectedLead?.convertedDate).format('LLL')}
                                </Text>
                            </div>
                        </div>
                    </div>}
                </>
            </DrawerComponent>
        </>
    );
}

export default Leads;